/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { AnimatePresence, motion } from "framer-motion";
import { UserWithRole } from "@/hooks/auth/types";
import { useLanguage } from "@/hooks/useLanguage";
import BackgroundPickerButton from "@/components/BackgroundPickerButton";
import NotificationIcon from "@/components/ui/NotificationIcon";

interface NavItem {
  label: string;
  href: string;
  subItems?: NavItem[];
}

const MacOSNavbar: React.FC = () => {
  // Auth & language hooks
  const { user, signOut } = useAuth();
  const { t, currentLanguage, switchLanguage } = useLanguage();
  const location = useLocation();
  const userWithRole = user as UserWithRole;

  // State variables
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Recreate nav items whenever the user role changes
  const navItems: NavItem[] = React.useMemo(() => {
    // Define the createNavItems function inside useMemo
    const createNavItems = () => {
      const baseNavItems = [
        {
          label: t("homePage"),
          href: `${
            userWithRole?.role === "admin" ? "/admin/home" : "/homepage"
          }`,
        },
        {
          label: t("courses"),
          href: `${
            userWithRole?.role === "admin" ? "/admin/courses" : "/courses"
          }`,
          subItems: [
            {
              label: t("myCourses"),
              href: `${
                userWithRole?.role === "admin" ? "/admin" : "/user"
              }/courses`,
            },
            {
              label: t("certificates"),
              // href: `${
              //   userWithRole?.role === "admin" ? "/admin" : "/user"
              // }/certificates`,
            },
          ],
        },
      ];

      // Add user-specific navigation items
      if (userWithRole?.role !== "admin") {
        baseNavItems.push({
          label: t("profile"),
          href: "/user/profile",
          subItems: [
            { label: t("personalInfo"), href: "/user/profile" },
            { label: t("editProfile"), href: "/user/profile/edit" },
          ],
        });

        // Messaging link disabled
        // baseNavItems.push({
        //   label: t("messages"),
        //   href: "/user/user-messaging",
        // });
      }

      // Add admin navigation items only for admin users
      if (userWithRole?.role === "admin") {
        baseNavItems.push({
          label: t("admin"),
          href: "/admin/home",
          subItems: [
            { label: t("homePage"), href: "/admin/home" },
            { label: t("widgets"), href: "/admin/home" },
            { label: t("courses"), href: "/admin/courses" },
            {
              label: t("sections"),
              href: "/admin/sections",
            },
            {
              label: t("departments"),
              href: "/admin/departments",
            },
            {
              label: t("mediaLibrary"),
              href: "/admin/media-library",
            },
            {
              label: t("translations"),
              href: "/admin/translations",
            },
            {
              label: t("usersManage"),
              href: "/admin/users",
            },
            // PDF Signature - temporarily disabled
            // {
            //   label: t("signature") || "التوقيع الإلكتروني",
            //   href: "/admin/signature",
            // },
          ],
        });
      }

      return baseNavItems;
    };

    // Call the function and return its result
    return createNavItems();
  }, [userWithRole?.role, t]);

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenSubmenu(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Handle submenu toggle
  const toggleSubmenu = (e: React.MouseEvent, label: string) => {
    e.stopPropagation();
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  // Check if route is active
  const isActive = (href: string) => {
    return (
      location.pathname === href || location.pathname.startsWith(`${href}/`)
    );
  };

  // Render user dropdown menu
  const renderUserDropdown = () => (
    <div className="relative">
      <button
        onClick={(e) => toggleSubmenu(e, "userMenu")}
        className="flex items-center space-x-2 rtl:space-x-reverse focus:outline-none"
      >
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white overflow-hidden">
          {user?.user_metadata?.avatar_url ? (
            <img
              src={user.user_metadata.avatar_url}
              alt={user.email || "User"}
              className="w-full h-full object-cover"
            />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </button>

      {/* User Dropdown */}
      <AnimatePresence>
        {openSubmenu === "userMenu" && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-1 right-0 rtl:right-auto rtl:left-0 bg-black/80 backdrop-blur-xl rounded-xl shadow-xl border border-white/10 p-2 min-w-[200px] z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-[-8px] right-3 rtl:right-auto rtl:left-3 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-black/80"></div>

            <div className="px-3 py-2 border-b border-white/10 mb-2">
              <p className="text-white font-medium text-sm">{user?.email}</p>
              <p className="text-white/60 text-xs">
                {user?.user_metadata?.full_name || "User"}
              </p>
            </div>

            <div className="space-y-1">
              <Link
                to={
                  userWithRole?.role === "admin"
                    ? "/admin/profilePreview"
                    : "/user/profile"
                }
                className="block px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                {t("profile")}
              </Link>
              <Link
                to="/user/settings"
                className="block px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                {t("settings")}
              </Link>
              <button
                onClick={() => signOut()}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                {t("signOut")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Render login/register buttons for non-logged-in users
  const renderAuthButtons = () => (
    <div className="flex items-center space-x-2 rtl:space-x-reverse">
      <Link
        to="/auth/login"
        className="text-sm px-3 py-1 text-white/80 hover:text-white transition-colors"
      >
        {t("login")}
      </Link>
      <Link
        to="/auth/register"
        className="text-sm px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        {t("register")}
      </Link>
    </div>
  );

  // Render language switcher
  const renderLanguageSwitcher = () => (
    <div className="relative">
      <button
        onClick={(e) => toggleSubmenu(e, "languageMenu")}
        className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 bg-white/10 backdrop-blur-xl hover:scale-105 border border-white/20"
        title={t("changeLanguage")}
        aria-label={t("changeLanguage")}
      >
        <span className="text-white text-sm font-medium uppercase">
          {currentLanguage}
        </span>
      </button>

      {/* Language Dropdown */}
      <AnimatePresence>
        {openSubmenu === "languageMenu" && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-1 right-0 rtl:right-auto rtl:left-0 bg-black/60 backdrop-blur-xl rounded-xl shadow-xl border border-white/10 p-2 z-50 w-40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-[-8px] right-3 rtl:right-auto rtl:left-3 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-black/60"></div>

            <div className="space-y-1">
              <button
                onClick={() => {
                  switchLanguage("ar");
                  setOpenSubmenu(null);
                }}
                className={`flex items-center w-full px-3 py-2 rounded-lg text-sm ${
                  currentLanguage === "ar"
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                } transition-colors`}
              >
                <span className="w-5 h-5 flex items-center justify-center me-2 bg-white/10 rounded-full text-xs">
                  ع
                </span>
                <span>العربية</span>
                {currentLanguage === "ar" && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-4 h-4 ml-auto"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
              <button
                onClick={() => {
                  switchLanguage("en");
                  setOpenSubmenu(null);
                }}
                className={`flex items-center w-full px-3 py-2 rounded-lg text-sm ${
                  currentLanguage === "en"
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                } transition-colors`}
              >
                <span className="w-5 h-5 flex items-center justify-center me-2 bg-white/10 rounded-full text-xs">
                  EN
                </span>
                <span>English</span>
                {currentLanguage === "en" && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-4 h-4 ml-auto"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Render search bar
  const renderSearchBar = () => (
    <div
      className={`relative transition-all duration-300 ${
        isSearchFocused ? "w-48 md:w-64" : "w-32 md:w-40"
      }`}
    >
      <input
        type="text"
        placeholder={t("search")}
        className="w-full bg-white/10 border border-white/10 rounded-full py-1 px-4 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-1 focus:ring-white/30 focus:bg-white/15"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onFocus={() => setIsSearchFocused(true)}
        onBlur={() => setIsSearchFocused(false)}
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-white/60 absolute top-1/2 right-3 rtl:right-auto rtl:left-3 transform -translate-y-1/2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
  );

  // Render mobile menu toggle button
  const renderMobileMenuToggle = () => (
    <button
      className="md:hidden text-white p-1 focus:outline-none"
      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
    >
      {isMobileMenuOpen ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      )}
    </button>
  );

  // Render logo
  const renderLogo = () => (
    <Link to="/" className="flex items-center">
      <img
        src="/images/logo/wide-logo.png"
        alt="Logo"
        className="w-40 h-auto"
      />
      <span className="text-white font-semibold text-sm ml-2 rtl:ml-0 rtl:mr-2 hidden sm:inline-block">
        {t("siteDepartment")}
      </span>
      <span className="text-red-500 font-bold text-sm ml-2 rtl:ml-0 rtl:mr-2 hidden sm:inline-block">
        {t("experimentalLaunch")}
      </span>
    </Link>
  );

  // Render navigation items
  const renderNavItems = () => (
    <div className="hidden md:flex items-center justify-center space-x-6 rtl:space-x-reverse flex-1 mx-4">
      {navItems.map((item) => (
        <div key={item.label} className="relative">
          {item.subItems ? (
            <button
              onClick={(e) => toggleSubmenu(e, item.label)}
              className={`text-sm px-3 py-1 rounded-full ${
                isActive(item.href)
                  ? "text-white bg-white/10"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              } transition-colors`}
            >
              {item.label}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`inline-block w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1 transition-transform ${
                  openSubmenu === item.label ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          ) : (
            <Link
              to={item.href}
              className={`text-sm px-3 py-1 rounded-full ${
                isActive(item.href)
                  ? "text-white bg-white/10"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              } transition-colors`}
            >
              {item.label}
            </Link>
          )}

          {/* Submenu */}
          <AnimatePresence>
            {item.subItems && openSubmenu === item.label && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full mt-1 left-0 rtl:left-auto rtl:right-0 bg-black/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 p-2 min-w-[180px] z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="absolute top-[-8px] left-3 rtl:left-auto rtl:right-3 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-black/80"></div>

                <div className="space-y-1">
                  {item.subItems.map((subItem) => (
                    <Link
                      key={subItem.label}
                      to={subItem.href}
                      className={`block px-3 py-2 rounded-lg text-sm ${
                        isActive(subItem.href)
                          ? "bg-white/10 text-white"
                          : "text-white/80 hover:bg-white/10 hover:text-white"
                      } transition-colors`}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 w-screen max-w-[100vw] ${
        isScrolled
          ? "backdrop-blur-xl bg-black/40"
          : "backdrop-blur-lg bg-black/30"
      }`}
      style={{
        width: "100vw",
        maxWidth: "100%",
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      {/* Apple-style top bar */}
      <div
        className="flex items-center h-12 px-4 md:px-6 lg:px-8 border-b border-white/10 w-screen"
        style={{ width: "100vw", maxWidth: "100%" }}
      >
        {currentLanguage === "en" ? (
          // English layout (reversed)
          <>
            {/* Right Controls (on left side in English) */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {user ? renderUserDropdown() : renderAuthButtons()}
              {renderSearchBar()}
              {user && <NotificationIcon />}
              <BackgroundPickerButton />
              {renderLanguageSwitcher()}
            </div>

            {/* Navigation - center */}
            {renderNavItems()}

            {/* Logo (on right side in English) */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {renderMobileMenuToggle()}
              {renderLogo()}
            </div>
          </>
        ) : (
          // Arabic layout (default)
          <>
            {/* Logo (on left side in Arabic) */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {renderMobileMenuToggle()}
              {renderLogo()}
            </div>

            {/* Navigation - center */}
            {renderNavItems()}

            {/* Right Controls */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              {renderLanguageSwitcher()}
              <BackgroundPickerButton />
              {user && <NotificationIcon />}
              {renderSearchBar()}
              {user ? renderUserDropdown() : renderAuthButtons()}
            </div>
          </>
        )}
      </div>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`md:hidden bg-black/80 backdrop-blur-xl w-screen border-b border-white/10 ${
              currentLanguage === "en" ? "text-left" : "text-right"
            }`}
            style={{ width: "100vw", maxWidth: "100%" }}
          >
            <div className="p-4 space-y-3">
              {navItems.map((item) => (
                <div key={item.label} className="w-full">
                  {item.subItems ? (
                    <div className="space-y-2">
                      <button
                        onClick={(e) => toggleSubmenu(e, item.label)}
                        className={`w-full py-2 px-3 rounded-lg hover:bg-white/10 transition-colors flex items-center ${
                          currentLanguage === "en"
                            ? "text-left justify-between"
                            : "text-right flex-row-reverse justify-between"
                        } text-white/80`}
                      >
                        <span>{item.label}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-4 h-4 transition-transform ${
                            openSubmenu === item.label ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {openSubmenu === item.label && (
                        <div
                          className={`space-y-1 ${
                            currentLanguage === "en"
                              ? "ml-4 border-l border-white/10 pl-2"
                              : "mr-4 border-r border-white/10 pr-2"
                          }`}
                        >
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.label}
                              to={subItem.href}
                              className={`block py-2 px-3 rounded-lg text-sm ${
                                isActive(subItem.href)
                                  ? "bg-white/10 text-white"
                                  : "text-white/70 hover:bg-white/5 hover:text-white"
                              } transition-colors ${
                                currentLanguage === "en"
                                  ? "text-left"
                                  : "text-right"
                              }`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block py-2 px-3 rounded-lg ${
                        isActive(item.href)
                          ? "bg-white/10 text-white"
                          : "text-white/80 hover:bg-white/10 hover:text-white"
                      } transition-colors ${
                        currentLanguage === "en" ? "text-left" : "text-right"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile Search */}
              <div className="pt-2 pb-1 border-t border-white/10">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t("search")}
                    className={`w-full bg-white/10 border border-white/10 rounded-lg py-2 px-4 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-1 focus:ring-white/30 ${
                      currentLanguage === "en" ? "text-left" : "text-right"
                    }`}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-white/60 absolute top-1/2 transform -translate-y-1/2 ${
                      currentLanguage === "en" ? "right-3" : "left-3"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Mobile language toggle */}
              <div className="pt-2 pb-1 border-t border-white/10">
                <div
                  className={`flex items-center mb-1 text-sm text-white/60 px-1 ${
                    currentLanguage === "en" ? "justify-start" : "justify-end"
                  }`}
                >
                  <span>{t("language")}</span>
                </div>
                <div
                  className={`flex gap-2 ${
                    currentLanguage === "en" ? "flex-row" : "flex-row-reverse"
                  }`}
                >
                  <button
                    onClick={() => {
                      switchLanguage("ar");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center flex-1 justify-center py-2 px-3 rounded-lg text-sm ${
                      currentLanguage === "ar"
                        ? "bg-white/20 text-white"
                        : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                    } transition-colors`}
                  >
                    العربية
                  </button>
                  <button
                    onClick={() => {
                      switchLanguage("en");
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center flex-1 justify-center py-2 px-3 rounded-lg text-sm ${
                      currentLanguage === "en"
                        ? "bg-white/20 text-white"
                        : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                    } transition-colors`}
                  >
                    English
                  </button>
                </div>
              </div>

              {/* Mobile User Area */}
              {user && (
                <div className="pt-2 border-t border-white/10">
                  <div
                    className={`flex items-center mb-2 ${
                      currentLanguage === "en"
                        ? "space-x-3 flex-row"
                        : "space-x-reverse space-x-3 flex-row-reverse"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white overflow-hidden flex-shrink-0">
                      {user.user_metadata?.avatar_url ? (
                        <img
                          src={user.user_metadata.avatar_url}
                          alt={user.email || "User"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div
                      className={
                        currentLanguage === "en" ? "text-left" : "text-right"
                      }
                    >
                      <p className="text-white text-sm font-medium">
                        {user.user_metadata?.full_name || "User"}
                      </p>
                      <p className="text-white/60 text-xs">{user.email}</p>
                    </div>
                  </div>

                  <div className="space-y-1 mt-3">
                    <Link
                      to="/user/profile"
                      className={`block w-full px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                        currentLanguage === "en" ? "text-left" : "text-right"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t("profile")}
                    </Link>
                    <Link
                      to="/user/settings"
                      className={`block w-full px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                        currentLanguage === "en" ? "text-left" : "text-right"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t("settings")}
                    </Link>
                    <button
                      onClick={() => {
                        signOut();
                        setIsMobileMenuOpen(false);
                      }}
                      className={`block w-full px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors ${
                        currentLanguage === "en" ? "text-left" : "text-right"
                      }`}
                    >
                      {t("signOut")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default MacOSNavbar;
